import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Stack,
  useTheme,
} from "@mui/material";
import { RemoveCircleOutline } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import useAdd from "../../../../../api/useAdd";
import useFetchObjects from "../../../../../api/useFetchObjects";
import { useAuth } from "../../../../../context";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const AddReturnToCompany = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();
  const theme = useTheme();

  // Fetch all approved purchases
  const { data: purchases } = useFetchObjects(
    ["purchases"],
    `purchases/?is_approved=2`,
    token
  );

  const [purchaseInput, setPurchaseInput] = useState(null);
  const [autocompleteInput, setAutocompleteInput] = useState("");
  const autocompleteRef = useRef();

  const [formErrors, setFormErrors] = useState({});
  const [input, setInput] = useState({
    purchase: "",
    items: [],
    total_amount: 0,
  });

  const { handleAdd, resetForm, loading } = useAdd(
    "return-to-company",
    token,
    t("pharmacy.returnAddedSuccess"),
    t("pharmacy.returnAddFailed")
  );

  // Reset on successful add
  useEffect(() => {
    if (resetForm) {
      setInput({ purchase: "", items: [], total_amount: 0 });
      setPurchaseInput(null);
      setFormErrors({});
    }
  }, [resetForm]);

  // When a purchase is selected, preload items
  useEffect(() => {
    if (purchaseInput) {
      const mapped = purchaseInput.items.map((it) => {
        const priceNum = parseFloat(it.purchase_price) || 0;
        const origQty = parseInt(it.quantity, 10) || 0;
        return {
          purchase_item: it.id,
          medicine: it.medicine,
          medicine_display: it.medicine_display,
          original_quantity: origQty,
          quantity: 0,
          purchase_price: priceNum.toFixed(2), // store as string
          total_price: 0,
        };
      });

      setInput({
        purchase: purchaseInput.id,
        items: mapped,
        total_amount: 0,
      });
      setFormErrors({});
    }
  }, [purchaseInput]);

  // Handle changes to quantity or unit price
  const handleChangeItem = (idx, field, raw) => {
    const items = [...input.items];
    let qty = items[idx].quantity;
    let priceStr = items[idx].purchase_price;

    if (field === "quantity") {
      const parsed = parseInt(raw, 10);
      qty = isNaN(parsed) || parsed < 0 ? 0 : parsed;
      items[idx].quantity = qty;
    }

    if (field === "purchase_price") {
      // allow blank while typing
      priceStr = raw;
      items[idx].purchase_price = priceStr;
    }

    // compute numeric price
    const numericPrice = parseFloat(priceStr) || 0;
    items[idx].total_price = numericPrice * qty;

    // recompute grand total
    const sum = items.reduce((s, it) => s + it.total_price, 0);

    setInput({
      ...input,
      items,
      total_amount: sum,
    });
  };

  const handleRemove = (idx) => {
    const items = [...input.items];
    items.splice(idx, 1);
    const sum = items.reduce((s, it) => s + it.total_price, 0);
    setInput({ ...input, items, total_amount: sum });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!input.purchase) {
      setFormErrors({ purchase: t("pharmacy.purchaseRequired") });
      return;
    }
    if (input.items.length === 0) {
      setFormErrors({ items: t("pharmacy.itemsRequired") });
      return;
    }

    const payload = {
      ...input,
      total_amount: input.total_amount.toFixed(2),
      items: input.items.map((it) => ({
        purchase_item: it.purchase_item,
        quantity: it.quantity,
        // ensure two decimals
        purchase_price: (parseFloat(it.purchase_price) || 0).toFixed(2),
      })),
    };

    if (await handleAdd(payload)) {
      navigate(-1);
    }
  };

  return (
    <Grid container justifyContent="center" mt={2} mb={5}>
      <Grid item xs={11} md={8}>
        <Box component="form" p={3} onSubmit={handleSave}>
          {/* Header */}
          <Box display="flex" alignItems="center" mb={3}>
            <Tooltip title={t("pharmacy.goBack")}>
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h5" fontWeight="bold" ml={2}>
              {t("pharmacy.addPurchaseReturn")}
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {/* Purchase selector */}
          <Autocomplete
            ref={autocompleteRef}
            options={purchases || []}
            getOptionLabel={(opt) =>
              `#${opt.id} – ${opt.medicine_company_display}`
            }
            value={purchaseInput}
            inputValue={autocompleteInput}
            onInputChange={(_, v) => setAutocompleteInput(v)}
            onChange={(_, v) => setPurchaseInput(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("pharmacy.selectPurchase")}
                error={!!formErrors.purchase}
                helperText={formErrors.purchase}
              />
            )}
            sx={{ mb: 4, backgroundColor: "#fff", borderRadius: 1 }}
          />

          {/* Items table */}
          <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: theme.palette.grey[100] }}>
                <TableRow>
                  <TableCell>{t("pharmacy.medicine")}</TableCell>
                  <TableCell align="center">
                    {t("pharmacy.purchasedQty")}
                  </TableCell>
                  <TableCell align="center">
                    {t("pharmacy.returnQty")}
                  </TableCell>
                  <TableCell align="center">
                    {t("pharmacy.unitPrice")}
                  </TableCell>
                  <TableCell align="center">
                    {t("pharmacy.totalPrice")}
                  </TableCell>
                  <TableCell align="center">{t("pharmacy.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {input.items.map((itm, i) => (
                  <TableRow key={i}>
                    <TableCell>{itm.medicine_display}</TableCell>
                    <TableCell align="center">
                      {itm.original_quantity}
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={itm.quantity}
                        inputProps={{
                          max: itm.original_quantity,
                          min: 0,
                        }}
                        onChange={(e) =>
                          handleChangeItem(i, "quantity", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="text"
                        size="small"
                        value={itm.purchase_price}
                        placeholder="0.00"
                        onChange={(e) =>
                          handleChangeItem(i, "purchase_price", e.target.value)
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      {itm.total_price.toFixed(2)}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="error" onClick={() => handleRemove(i)}>
                        <RemoveCircleOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              {t("pharmacy.totalAmount")}: {input.total_amount.toFixed(2)}
            </Typography>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: theme.palette.primary.main,
                "&:hover": { bgcolor: theme.palette.primary.dark },
              }}
            >
              {loading ? t("pharmacy.saving") : t("pharmacy.save")}
            </Button>
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
};

export default AddReturnToCompany;
